import { isSupportedCountry } from '@forto-dev/phoenix-ui-core'

import { BookingDTO } from '@/clients/booking'
import { PickupDeliveryLocation } from '@/features/locations'
import { getFullAddress, Timeline, TimelineMilestoneData } from '@/features/shipments'

import {
  getDeliveryAddress,
  getDestinationPort,
  getIsMainCarriageHandledByThirdParty,
  getOriginPort,
  getPickupAddress,
} from './waypoints'

export type ResolvedAddressMap = Map<string, PickupDeliveryLocation>

export type GetBookingTimelineArgs = Pick<
  BookingDTO,
  | 'waypoints'
  | 'cargoReadyAt'
  | 'departureAt'
  | 'arrivalAt'
  | 'deliveryDueAt'
  | 'originPortName'
  | 'destinationPortName'
> & {
  destinationCountryCode?: string
  resolvedAddresses?: ResolvedAddressMap
}

/**
 * Gets the location string and country code for an address.
 * Falls back to resolved address from addressID if place is not available.
 */
const getAddressInfo = (
  address: ReturnType<typeof getPickupAddress>,
  resolvedAddresses?: ResolvedAddressMap
): { location: string; countryCode?: string } => {
  // First try the legacy place data
  if (address?.place?.country) {
    return {
      location: getFullAddress(address.place),
      countryCode: address.place.countryCode?.toUpperCase(),
    }
  }

  // Fall back to resolved address from addressID
  if (address?.addressID && resolvedAddresses) {
    const resolved = resolvedAddresses.get(address.addressID)
    if (resolved) {
      return {
        location: resolved.addressFormatted,
        countryCode: resolved.address.countryCode?.toUpperCase(),
      }
    }
  }

  return { location: '—', countryCode: undefined }
}

export const getBookingTimeline = ({
  waypoints,
  cargoReadyAt,
  departureAt,
  arrivalAt,
  deliveryDueAt,
  originPortName,
  destinationPortName,
  destinationCountryCode,
  resolvedAddresses,
}: GetBookingTimelineArgs): Timeline => {
  const pickupAddress = getPickupAddress(waypoints)
  const deliveryAddress = getDeliveryAddress(waypoints)
  const originPort = getOriginPort(waypoints)
  const destinationPort = getDestinationPort(waypoints)

  const originAddressInfo = getAddressInfo(pickupAddress, resolvedAddresses)
  const destinationAddressInfo = getAddressInfo(deliveryAddress, resolvedAddresses)
  const arrivalCountryCode = destinationCountryCode?.toUpperCase()

  const isMainCarriageHandledByThirdParty = getIsMainCarriageHandledByThirdParty(waypoints)

  return {
    origin: {
      ...defaultTimelineMilestone,
      date: new Date(cargoReadyAt),
      location: originAddressInfo.location,
      flag:
        originAddressInfo.countryCode && isSupportedCountry(originAddressInfo.countryCode)
          ? originAddressInfo.countryCode
          : undefined,
    },
    departure: {
      ...defaultTimelineMilestone,
      date: departureAt ? new Date(departureAt) : undefined,
      location: `${originPortName} (${originPort}) `,
      isHandlingByThirdParty: isMainCarriageHandledByThirdParty,
    },
    arrival: {
      ...defaultTimelineMilestone,
      date: arrivalAt ? new Date(arrivalAt) : undefined,
      location: `${destinationPortName} (${destinationPort})`,
      flag: arrivalCountryCode && isSupportedCountry(arrivalCountryCode) ? arrivalCountryCode : undefined,
      isHandlingByThirdParty: isMainCarriageHandledByThirdParty,
    },
    delivery: {
      ...defaultTimelineMilestone,
      date: deliveryDueAt ? new Date(deliveryDueAt) : undefined,
      location: destinationAddressInfo.location,
      flag:
        destinationAddressInfo.countryCode && isSupportedCountry(destinationAddressInfo.countryCode)
          ? destinationAddressInfo.countryCode
          : undefined,
    },
  }
}

const defaultTimelineMilestone: TimelineMilestoneData = {
  dateStatus: 'estimated',
  progress: 0,
  date: undefined,
  location: '—',
  outgoingConnections: [],
}
